import { useRef, useState, type MouseEvent } from 'react';
import { sx } from '../../ui/designStyles';
import { BuildLabel } from '../../ui/buildLabels';
import type { FpActs, FpModel, FpUi } from './familyVals';

// V7 Family Portal "Today with <resident>" (Aithora ConsoleV7.dc.html T19042, care-flow.js
// AithoraCF.family). Every line comes from the portal's own routes; where a route does not
// carry what the design shows, the card says so instead of showing the design's sample:
//   - the day in plain words: the care team's latest note (GET /family/:id/portal careNote);
//   - "This week": the care team's summary (portal summary);
//   - things that need you: medication changes the family has not seen (notices, med_change),
//     "Ask why" sends the question to the nurse (POST /family/:id/messages); forms to sign have
//     no e-sign route yet, so none are listed;
//   - family circle: GET /family/:id/circle/access; the shared to-do: portal tasks (claim);
//   - send to her screen: a photo or a voice note as a family moment (POST /family/:id/moments).
// Video calls to her room screen have no route: the button says so when pressed.

const BTN = 'display:flex;align-items:center;justify-content:center;gap:7px;min-height:44px;padding:0 12px;border-radius:12px;font-size:16px;font-weight:800;text-decoration:none;text-align:center;box-sizing:border-box;cursor:pointer;font-family:inherit;white-space:nowrap;';
const BLUE = BTN + 'color:#FFFFFF;background:#264C7E;border:0;';
const LINE = BTN + 'color:#264C7E;background:#FFFFFF;border:1px solid rgba(47,92,151,.45);';
const DARK = BTN + 'color:#FFFFFF;background:#22272E;border:0;min-height:36px;flex:0 0 auto;';
const HEAD = 'font-size:16px;letter-spacing:.09em;text-transform:uppercase;font-weight:700;color:#3C454F;';

const clock = (ts: number) => new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

function Btn({ css, onClick, disabled, title, children, label, ink }: { ink?: boolean; css: string; onClick: () => void; disabled?: boolean; title?: string; children: React.ReactNode; label?: string }) {
  return (
    <a
      href="#"
      role="button"
      className={ink ? 'cf-ink' : undefined}
      aria-disabled={disabled || undefined}
      aria-label={label}
      title={title}
      onClick={(e: MouseEvent) => {
        e.preventDefault();
        if (!disabled) onClick();
      }}
      style={sx(css + (disabled ? 'opacity:.55;cursor:not-allowed;' : ''))}
    >
      {children}
    </a>
  );
}

export interface TodayCardState {
  asked: Record<string, number>;
  call: number | null;
}

export function FpTodayCard({
  M,
  act,
  goTab,
  readOnly,
  readOnlyReason,
  real,
}: {
  M: FpModel;
  act: FpActs;
  goTab: (t: FpUi['tab']) => void;
  readOnly: boolean;
  readOnlyReason: string | null;
  /** The portal route's honesty flag: the SAMPLE label shows only when it says the data is not real. */
  real: boolean | null;
}) {
  const [st, setSt] = useState<TodayCardState>({ asked: {}, call: null });
  const file = useRef<HTMLInputElement | null>(null);
  const R = M.resident;
  if (!R) return null;
  const first = R.first;
  const ro = readOnly;
  const why = readOnlyReason ?? undefined;
  // The day, in the care team's own words; a head only when there is a note to stand on.
  const note = M.careNote && M.careNote !== '–' ? M.careNote : '';
  const ordinary = M.panels.length > 0;
  const urgentToday = M.events.some((e) => e.urgent);
  const head = ordinary ? `${first} is having ${M.panels[0]?.value?.match(/an? (.*?) day/)?.[0] ?? 'an ordinary day'}.` : note ? `Today with ${first}` : `Nothing from the care team about ${first} yet today`;
  const headInk = ordinary && !urgentToday ? '#1F6B52' : '#22272E';
  const week = M.summary && M.summary !== '–' ? M.summary : '';
  // Medication changes the family has not marked seen: "Ask why" goes to the nurse as a message.
  const needs = M.notices.filter((n) => n.med && !n.mine).slice(0, 3);
  const openN = needs.filter((n) => !st.asked[n.id]).length;
  const task = M.tasks.find((t) => !t.done) ?? null;
  const circle = M.circle.slice(0, 4);
  const sendNote = M.photoSent
    ? `Sent to her screen at ${clock(Date.now())}. We’ll tell you when she sees it.`
    : M.photoNote ?? (M.resident?.atHome ? 'A photo or voice note shows up on her Aithora Home screen.' : 'A photo or voice note shows up on her Atlas screen.');
  return (
    <div data-screen-label="Today with resident" style={sx('grid-column:1/-1;box-sizing:border-box;display:flex;flex-direction:column;gap:14px;padding:16px 18px;border-radius:14px;background:#FFFFFF;border:1px solid rgba(34,39,46,.14);min-width:0;')}>
      <div style={sx('display:flex;align-items:flex-start;gap:16px;min-width:0;')}>
        <span aria-hidden="true" style={sx('width:60px;height:60px;border-radius:50%;background:#22272E;color:#FCFAF4;display:flex;align-items:center;justify-content:center;font-size:21px;font-weight:800;flex:0 0 auto;')}>
          {R.initials}
        </span>
        <div style={sx('flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:4px;')}>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
            <span style={sx(HEAD)}>Today</span>
            {real === false && (
              <BuildLabel>
                <span style={sx('font-size:14px;font-weight:800;letter-spacing:.08em;color:#54606C;border:1px solid rgba(34,39,46,.25);border-radius:4px;padding:1px 5px;')}>SAMPLE</span>
              </BuildLabel>
            )}
            <span style={sx('font-size:16px;color:#54606C;')}>from staff notes</span>
          </div>
          <div style={sx(`font-size:24px;font-weight:800;letter-spacing:-.02em;line-height:1.2;color:${headInk};text-wrap:pretty;`)}>{head}</div>
          {note && <div style={sx('font-size:15.5px;line-height:1.5;color:#22272E;text-wrap:pretty;')}>{note}</div>}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              goTab('health');
            }}
            style={sx('font-size:16px;line-height:1.45;color:#2F5C97;font-weight:600;text-decoration:none;')}
          >
            {week ? `This week: ${week} ` : ''}
            <b>See her week &#8594;</b>
          </a>
        </div>
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;')}>
        <Btn ink css={BLUE} onClick={() => setSt((s) => ({ ...s, call: Date.now() }))} disabled={ro} title={why}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M23 7l-7 5 7 5V7zM1 5h15v14H1z" />
          </svg>
          Video call
        </Btn>
        <Btn css={LINE} onClick={() => goTab('messages')}>
          Message the nurse
        </Btn>
        <Btn css={LINE} onClick={() => goTab('visits')}>
          Plan a visit
        </Btn>
      </div>
      {st.call !== null && (
        <div role="status" style={sx('font-size:16px;font-weight:700;color:#7A4A0E;')}>
          Video calls to her room screen are not connected yet. Message the nurse to set up a call.
        </div>
      )}
      <div style={sx('display:flex;flex-direction:column;gap:0;border-top:1px solid rgba(34,39,46,.1);padding-top:10px;')}>
        <span style={sx(`font-size:16px;font-weight:800;color:${openN ? '#7A4A0E' : '#1F6B52'};`)}>
          {openN ? (openN === 1 ? '1 thing needs you' : `${openN} things need you`) : 'Nothing needs you right now'}
        </span>
        {needs.map((n) => {
          const asked = st.asked[n.id];
          return (
            <div key={n.id} style={sx('display:flex;align-items:center;gap:12px;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.08);min-width:0;flex-wrap:wrap;')}>
              <span style={sx('flex:1 1 220px;min-width:0;display:flex;flex-direction:column;gap:2px;')}>
                <span style={sx('font-size:16px;font-weight:700;color:#22272E;line-height:1.3;')}>{n.k}</span>
                <span style={sx('font-size:16px;color:#54606C;line-height:1.4;')}>{[n.when, n.told].filter(Boolean).join(' · ')}</span>
              </span>
              {!asked ? (
                <Btn
                  css={DARK}
                  disabled={ro}
                  title={why}
                  onClick={() => {
                    act.ask(`About the change on ${n.when} (${n.k.toLowerCase()}): why was it changed, and what should we watch for?`);
                    setSt((s) => ({ ...s, asked: { ...s.asked, [n.id]: Date.now() } }));
                  }}
                >
                  Ask why
                </Btn>
              ) : (
                <span style={sx('font-size:16px;font-weight:700;color:#1F6B52;white-space:nowrap;')}>&#10003; {`Question sent to the nurse · ${clock(asked)}`}</span>
              )}
            </div>
          );
        })}
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:18px;')}>
        <div style={sx('display:flex;flex-direction:column;gap:6px;min-width:0;')}>
          <span style={sx(HEAD)}>Family circle</span>
          {circle.length === 0 && <span style={sx('font-size:14px;color:#54606C;')}>Nobody else in the family is linked yet.</span>}
          {circle.map((p, i) => (
            <div key={i} style={sx('display:flex;align-items:baseline;gap:8px;font-size:14px;flex-wrap:wrap;')}>
              <b style={sx('color:#22272E;')} translate={p.mine ? undefined : 'no'}>
                {p.mine ? 'You' : p.who}
              </b>
              <span style={sx('color:#54606C;')}>{p.lastShort}</span>
            </div>
          ))}
          <div style={sx('display:flex;align-items:center;gap:10px;margin-top:4px;padding:8px 10px;border-radius:10px;background:rgba(34,39,46,.05);flex-wrap:wrap;')}>
            {task ? (
              <>
                <span style={sx('flex:1 1 140px;min-width:0;display:flex;flex-direction:column;')}>
                  <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{task.t}</span>
                  <span style={sx(`font-size:16px;font-weight:700;color:${task.who ? '#1F6B52' : '#7A4A0E'};`)}>
                    {task.mine ? 'You’re on it' : task.who ? `${task.who} is on it` : 'Nobody has taken this'}
                  </span>
                </span>
                {!task.who && (
                  <Btn css={LINE + 'min-height:34px;flex:0 0 auto;'} disabled={ro} title={why} onClick={() => act.taskAct(task.id, 'claim')}>
                    I&rsquo;ll do it
                  </Btn>
                )}
                {task.mine && (
                  <Btn css={LINE + 'min-height:34px;flex:0 0 auto;'} disabled={ro} title={why} onClick={() => act.taskAct(task.id, 'done')}>
                    Done
                  </Btn>
                )}
              </>
            ) : (
              <span style={sx('flex:1 1 140px;min-width:0;font-size:16px;color:#54606C;')}>No shared to-dos yet. Add one on To do.</span>
            )}
          </div>
        </div>
        <div style={sx('display:flex;flex-direction:column;gap:6px;min-width:0;')}>
          <span style={sx(HEAD)}>Send to her screen</span>
          <span style={sx(`font-size:16px;line-height:1.45;color:${M.photoNote && !M.photoSent ? '#8A5A00' : '#3C454F'};`)}>{sendNote}</span>
          <input
            ref={file}
            type="file"
            accept="image/*,audio/*"
            aria-label="Choose a photo or voice note"
            style={sx('display:none;')}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) act.photo(f);
              e.target.value = '';
            }}
          />
          <Btn css={LINE} disabled={ro} title={why} onClick={() => file.current?.click()}>
            {M.photoSent ? 'Send another' : 'Send a photo or voice note'}
          </Btn>
        </div>
      </div>
    </div>
  );
}
